import React, { useState } from 'react';
import {
  Paper,
  Title,
  Text,
  Group,
  Button,
  Table,
  ActionIcon,
  Select,
  Stack,
  Card,
  SimpleGrid,
  Avatar,
  Switch,
  TextInput,
  Modal,
  Textarea,
  Alert,
  Progress,
  Tabs,
  Timeline,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconUser,
  IconUsers,
  IconDownload,
  IconPrinter,
  IconSearch,
  IconRefresh,
  IconEdit,
  IconHistory,
  IconNotes,
  IconBell,
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  useTeacherClasses,
  useClassAttendance,


} from '../../hooks/useTeacher';
import { SmartLoader } from '../../components/common/LoadingStates';

interface AttendanceRecord {
  id: string;
  student_id: string;
  student_name: string;
  status: 'present' | 'absent' | 'late' | 'excused';
  check_in_time?: string;
  check_out_time?: string;
  notes?: string;
  parent_notified?: boolean;
}

const AttendancePage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string | null>('attendance');
  const [notesModalOpened, { open: openNotesModal, close: closeNotesModal }] = useDisclosure(false);
  const [selectedStudent, setSelectedStudent] = useState<AttendanceRecord | null>(null);
  const [attendanceNotes, setAttendanceNotes] = useState('');

  const { data: classes } = useTeacherClasses();
  const {
    data: attendanceData,
    isLoading: attendanceLoading,
    error: attendanceError,
    refetch: refetchAttendance
  } = useClassAttendance(
    selectedClassId || undefined,
    selectedDate || new Date()
  );

  // Enhanced data fetching using real API
  // const {
  //   data: todaySummary,
  //   isLoading: summaryLoading,
  // } = useTodayClassSummary();

  // const {
  //   data: classRoster,
  //   isLoading: rosterLoading,
  // } = useTeacherClassRoster(selectedClassId || undefined, {
  //   active_only: true,
  // });

  // const generateAttendanceReport = useGenerateAttendanceReport();

  const attendance = attendanceData?.attendance || [];

  // Generate attendance report handler
  // const handleGenerateReport = async () => {
  //   try {
  //     await generateAttendanceReport.mutateAsync({
  //       date: selectedDate?.toISOString().split('T')[0],
  //       class_id: selectedClassId || undefined,
  //     });
  //   } catch (error) {
  //     console.error('Failed to generate attendance report:', error);
  //   }
  // };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'green';
      case 'absent': return 'red';
      case 'late': return 'yellow';
      case 'excused': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present': return <IconCheck size={16} />;
      case 'absent': return <IconX size={16} />;
      case 'late': return <IconClock size={16} />;
      case 'excused': return <IconAlertCircle size={16} />;
      default: return <IconUser size={16} />;
    }
  };

  const handleStatusChange = async (_studentId: string, _newStatus: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    notifications.show({
      title: 'Attendance Updated',
      message: 'Student attendance status has been updated',
      color: 'green',
    });

    refetchAttendance();
  };

  const handleAddNotes = (student: AttendanceRecord) => {
    setSelectedStudent(student);
    setAttendanceNotes(student.notes || '');
    openNotesModal();
  };

  const handleSaveNotes = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    notifications.show({
      title: 'Notes Saved',
      message: 'Attendance notes have been saved',
      color: 'green',
    });

    closeNotesModal();
    refetchAttendance();
  };

  const handleNotifyParent = async (_studentId: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    notifications.show({
      title: 'Parent Notified',
      message: 'Notification sent to parent successfully',
      color: 'green',
      icon: <IconBell size={18} />,
    });

    refetchAttendance();
  };

  const stats = {
    total: attendance.length,
    present: attendance.filter((a: any) => a.status === 'present').length,
    absent: attendance.filter((a: any) => a.status === 'absent').length,
    late: attendance.filter((a: any) => a.status === 'late').length,
    excused: attendance.filter((a: any) => a.status === 'excused').length,
  };

  const attendanceRate = stats.total > 0 ? (stats.present / stats.total) * 100 : 0;

  const filteredAttendance = attendance.filter((record: any) =>
    record.student_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Mock timeline data
  const timelineData = [
    { time: '08:00 AM', event: 'Attendance started', icon: IconClock },
    { time: '08:15 AM', event: '18 students checked in', icon: IconCheck },
    { time: '08:30 AM', event: '3 students marked late', icon: IconAlertCircle },
    { time: '09:00 AM', event: 'Parent notification sent for absent students', icon: IconBell },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>Attendance Management</Title>
          <Text size="sm" c="dimmed">
            Track and manage daily student attendance
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="light"
          >
            Export Report
          </Button>
          <Button
            leftSection={<IconPrinter size={16} />}
            variant="light"
          >
            Print
          </Button>
        </Group>
      </Group>

      {/* Date and Class Selection */}
      <Card withBorder>
        <Group>
          <DatePickerInput
            label="Select Date"
            placeholder="Pick date"
            value={selectedDate}
            onChange={setSelectedDate}
            leftSection={<IconCalendar size={16} />}
            style={{ flex: 1 }}
            maxDate={new Date()}
          />
          <Select
            label="Select Class"
            placeholder="Choose a class"
            data={classes?.filter(cls => cls && cls.name).map(cls => ({
              value: cls.id.toString(),
              label: cls.name,
            })) || []}
            value={selectedClassId}
            onChange={setSelectedClassId}
            leftSection={<IconUsers size={16} />}
            style={{ flex: 1 }}
          />
          <ActionIcon
            variant="filled"
            size="lg"
            mt={25}
            onClick={() => refetchAttendance()}
          >
            <IconRefresh size={20} />
          </ActionIcon>
        </Group>
      </Card>

      {/* Statistics */}
      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="md">
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Students</Text>
          <Text fw={700} size="xl">{stats.total}</Text>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Present</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="green">{stats.present}</Text>
            <ThemeIcon color="green" variant="light" size="sm">
              <IconCheck size={14} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Absent</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="red">{stats.absent}</Text>
            <ThemeIcon color="red" variant="light" size="sm">
              <IconX size={14} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Late</Text>
          <Group gap="xs">
            <Text fw={700} size="xl" c="yellow">{stats.late}</Text>
            <ThemeIcon color="yellow" variant="light" size="sm">
              <IconClock size={14} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Attendance Rate</Text>
          <Stack gap="xs">
            <Text fw={700} size="xl">{attendanceRate.toFixed(1)}%</Text>
            <Progress value={attendanceRate} color="blue" size="sm" />
          </Stack>
        </Card>
      </SimpleGrid>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="attendance" leftSection={<IconUsers size={16} />}>
            Attendance List
          </Tabs.Tab>
          <Tabs.Tab value="timeline" leftSection={<IconHistory size={16} />}>
            Timeline
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="attendance" pt="lg">
          <SmartLoader
            isLoading={attendanceLoading}
            error={attendanceError as Error | null}
            isEmpty={!filteredAttendance.length}
            emptyState={
              <Alert color="blue" title="No attendance records">
                {selectedClassId
                  ? "No attendance records found for the selected date and class."
                  : "Please select a class to view attendance records."}
              </Alert>
            }
          >
            <Stack gap="md">
              <TextInput
                placeholder="Search students..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <Paper withBorder radius="md">
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Student</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Check In</Table.Th>
                      <Table.Th>Check Out</Table.Th>
                      <Table.Th>Notes</Table.Th>
                      <Table.Th>Parent Notified</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredAttendance.map((record: any) => (
                      <Table.Tr key={record.id}>
                        <Table.Td>
                          <Group gap="sm">
                            <Avatar size="sm" radius="xl">
                              {record.student_name.split(' ').map((n: string) => n[0]).join('')}
                            </Avatar>
                            <Text size="sm" fw={500}>{record.student_name}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Select
                            data={[
                              { value: 'present', label: 'Present' },
                              { value: 'absent', label: 'Absent' },
                              { value: 'late', label: 'Late' },
                              { value: 'excused', label: 'Excused' },
                            ]}
                            value={record.status}
                            onChange={(value) => handleStatusChange(record.student_id, value || 'present')}
                            leftSection={getStatusIcon(record.status)}
                            styles={{
                              input: {
                                color: `var(--mantine-color-${getStatusColor(record.status)}-6)`,
                              },
                            }}
                            w={120}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {record.check_in_time || '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {record.check_out_time || '-'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant={record.notes ? 'filled' : 'light'}
                            onClick={() => handleAddNotes(record)}
                          >
                            <IconNotes size={16} />
                          </ActionIcon>
                        </Table.Td>
                        <Table.Td>
                          {record.status === 'absent' || record.status === 'late' ? (
                            <Switch
                              checked={record.parent_notified || false}
                              onChange={() => handleNotifyParent(record.student_id)}
                              size="sm"
                            />
                          ) : (
                            <Text size="sm" c="dimmed">N/A</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleAddNotes(record)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Stack>
          </SmartLoader>
        </Tabs.Panel>

        <Tabs.Panel value="timeline" pt="lg">
          <Card withBorder>
            <Title order={4} mb="md">Attendance Timeline</Title>
            <Timeline active={3} bulletSize={24} lineWidth={2}>
              {timelineData.map((item, index) => (
                <Timeline.Item
                  key={index}
                  bullet={<item.icon size={12} />}
                  title={item.event}
                >
                  <Text c="dimmed" size="sm">{item.time}</Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Tabs.Panel>
      </Tabs>

      {/* Notes Modal */}
      <Modal
        opened={notesModalOpened}
        onClose={closeNotesModal}
        title={`Attendance Notes - ${selectedStudent?.student_name}`}
      >
        <Stack gap="md">
          <Textarea
            label="Notes"
            placeholder="Add any relevant notes about the student's attendance..."
            value={attendanceNotes}
            onChange={(e) => setAttendanceNotes(e.target.value)}
            rows={4}
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={closeNotesModal}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotes}>
              Save Notes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default AttendancePage;
